// Inbox, Reiter Emails (Runde 6, 6D, I-04, I03): Mails aus dem Postfach-Abgleich (6A), die eine Entscheidung brauchen oder noch
// niemand gelesen hat. Zeilen wie die Aufgaben der Inbox; ein Klick klappt die Mail mit Text und Anhängen auf, dort lässt sich
// die Mail einem Lead zuordnen (oder bei mehreren Treffern einer der Kandidaten wählen), ignorieren und als gelesen markieren.
// Antworten läuft über den Lead (Komposer im Feed, 6B). Filter: Offen (unzugeordnet oder mehrdeutig), Ungelesen, Alle.
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import type { SuchTreffer } from "../../api";
import { useEchtzeit } from "../../api/echtzeit";
import { mailEingangApi, type MailDetail, type MailZaehler, type MailZeile, type MailZuordnung } from "../../api/mailEingang";
import { useMeta } from "../../daten/meta";
import { useNeuVerbunden } from "../../daten/verbindung";
import { useTasten } from "../../werkzeuge/tasten";
import { zeitpunktLang } from "../../werkzeuge/zeit";
import { Laden, Leer } from "../Bausteine";
import { FehlerBox } from "../Fehler";
import { Icon } from "../Icon";
import { useMeldung } from "../Meldungen";
import { LeadWahl } from "./LeadWahl";
import { einzeilig, nachrichtZeit } from "./nachrichtenLogik";
import "./nachrichten.css";

type MailFilter = "offen" | "ungelesen" | "alle";

const FILTER: { wert: MailFilter; label: string; titel: string }[] = [
  { wert: "offen", label: "Offen", titel: "Mails, die keinem Lead zugeordnet werden konnten oder mehrdeutig sind" },
  { wert: "ungelesen", label: "Ungelesen", titel: "Eingegangene Mails, die noch niemand geöffnet hat" },
  { wert: "alle", label: "Alle", titel: "Alle abgeglichenen Mails" },
];

const OFFEN: MailZuordnung[] = ["unzugeordnet", "mehrdeutig"];

const ZUORDNUNG_TEXT: Record<MailZuordnung, string> = {
  zugeordnet: "Zugeordnet",
  mehrdeutig: "Mehrdeutig",
  unzugeordnet: "Nicht zugeordnet",
  intern: "Intern",
  ignoriert: "Ignoriert",
};

interface Props {
  wer: string;
  zaehler: MailZaehler | null;
  onZaehler: () => void;
}

export function MailReiter({ wer, zaehler, onZaehler }: Props) {
  const navigate = useNavigate();
  const meldung = useMeldung();
  const { nutzer } = useAuth();
  const meta = useMeta();
  const [params, setParams] = useSearchParams();
  const filter = (FILTER.some((f) => f.wert === params.get("filter")) ? params.get("filter") : "offen") as MailFilter;
  const [liste, setListe] = useState<MailZeile[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<unknown>(null);
  const [fokus, setFokus] = useState(0);
  const [offenId, setOffenId] = useState<string | null>(null);
  const [suche, setSuche] = useState(params.get("q") ?? "");
  const [suchwort, setSuchwort] = useState(suche);
  const generation = useRef(0);

  // Postfach: eigenes (Standard), das einer anderen Person oder alle
  const postfach = wer === "all" ? undefined : wer === "me" || wer === nutzer?.close_user_id || wer === nutzer?.id ? nutzer?.id : (meta.nutzer(wer)?.id ?? wer);

  const setParam = (k: string, v: string | null) => {
    const neu = new URLSearchParams(params);
    if (v) neu.set(k, v);
    else neu.delete(k);
    setParams(neu, { replace: true });
  };

  useEffect(() => {
    const t = window.setTimeout(() => setSuchwort(suche.trim()), 300);
    return () => window.clearTimeout(t);
  }, [suche]);
  useEffect(() => setOffenId(null), [filter, wer, suchwort]);

  const abfrage = useCallback(
    () => ({
      zuordnung: filter === "offen" ? OFFEN : undefined,
      nur_ungelesen: filter === "ungelesen" || undefined,
      user_id: postfach,
      q: suchwort || undefined,
      limit: 100,
    }),
    [filter, postfach, suchwort],
  );

  const laden = useCallback(
    async (still = false) => {
      const gen = ++generation.current;
      if (!still) setLaedt(true);
      setFehler(null);
      try {
        const r = await mailEingangApi.liste(abfrage());
        if (gen !== generation.current) return;
        setListe(r.data);
        setCursor(r.has_more ? r.cursor : null);
        if (!still) setFokus(0);
      } catch (e) {
        if (gen === generation.current) setFehler(e);
      } finally {
        if (gen === generation.current) setLaedt(false);
      }
    },
    [abfrage],
  );

  useEffect(() => {
    void laden();
  }, [laden]);

  const zeit = useRef<number | undefined>(undefined);
  useEchtzeit(["mail.received", "mail.updated"], () => {
    window.clearTimeout(zeit.current);
    zeit.current = window.setTimeout(() => {
      void laden(true);
      onZaehler();
    }, 400);
  });
  useNeuVerbunden(() => void laden(true));
  useEffect(() => () => window.clearTimeout(zeit.current), []);

  const mehr = async () => {
    if (!cursor) return;
    try {
      const r = await mailEingangApi.liste({ ...abfrage(), cursor });
      setListe((l) => [...l, ...r.data.filter((x) => !l.some((y) => y.id === x.id))]);
      setCursor(r.has_more ? r.cursor : null);
    } catch (e) {
      setFehler(e);
    }
  };

  const uebernehmen = useCallback(
    (neu: MailZeile) => {
      const passt = filter === "alle" || (filter === "offen" ? OFFEN.includes(neu.zuordnung) : !neu.gelesen);
      setListe((l) => l.flatMap((x) => (x.id !== neu.id ? [x] : passt || neu.id === offenId ? [{ ...x, ...neu }] : [])));
    },
    [filter, offenId],
  );

  const gelesenSetzen = async (m: MailZeile, gelesen: boolean) => {
    if (m.richtung !== "eingehend" || m.gelesen === gelesen) return;
    uebernehmen({ ...m, gelesen });
    try {
      const r = await mailEingangApi.gelesen(m.id, gelesen);
      uebernehmen(r.nachricht);
      onZaehler();
    } catch (e) {
      uebernehmen(m);
      meldung.fehler(e, "Nicht gespeichert");
    }
  };

  const leadOeffnen = (leadId: string) => navigate(`/lead/${encodeURIComponent(leadId)}`);

  const oeffnen = (m: MailZeile, i: number) => {
    setFokus(i);
    if (offenId === m.id) {
      setOffenId(null);
      return;
    }
    setOffenId(m.id);
    if (!m.gelesen) void gelesenSetzen(m, true);
  };

  const aktuelle = liste[fokus];
  useTasten({
    j: () => setFokus((f) => Math.min(f + 1, liste.length - 1)),
    ArrowDown: () => setFokus((f) => Math.min(f + 1, liste.length - 1)),
    k: () => setFokus((f) => Math.max(f - 1, 0)),
    ArrowUp: () => setFokus((f) => Math.max(f - 1, 0)),
    Enter: () => aktuelle && oeffnen(aktuelle, fokus),
    "mod+Enter": () => aktuelle?.lead_id && window.open(`/lead/${encodeURIComponent(aktuelle.lead_id)}`, "_blank", "noopener"),
    u: () => aktuelle && void gelesenSetzen(aktuelle, !aktuelle.gelesen),
    Escape: () => offenId && setOffenId(null),
  });

  useEffect(() => {
    document.querySelector(`[data-mail="${liste[fokus]?.id}"]`)?.scrollIntoView({ block: "nearest" });
  }, [fokus, liste]);

  const zahl = (f: MailFilter): number | null => (!zaehler ? null : f === "offen" ? zaehler.offen : f === "ungelesen" ? zaehler.ungelesen : null);

  const leerTexte: Record<MailFilter, { titel: string; text: string }> = {
    offen: { titel: "Alles zugeordnet.", text: "Keine Mails, die auf eine Entscheidung warten." },
    ungelesen: { titel: "Nichts Ungelesenes.", text: "Alle eingegangenen Mails sind gelesen." },
    alle: { titel: "Noch keine Mails.", text: "Mails erscheinen hier, sobald der Postfach-Abgleich unter Einstellungen › E-Mail eingeschaltet ist." },
  };

  return (
    <>
      <div className="liste-leiste inbox-leiste sms-leiste">
        <div className="sms-filter" role="tablist" aria-label="Filter">
          {FILTER.map((f) => {
            const n = zahl(f.wert);
            return (
              <button
                key={f.wert}
                type="button"
                role="tab"
                aria-selected={filter === f.wert}
                title={f.titel}
                className={`sms-filter__knopf${filter === f.wert ? " sms-filter__knopf--an" : ""}`}
                onClick={() => setParam("filter", f.wert === "offen" ? null : f.wert)}
              >
                {f.label}
                {n ? <span className="sms-filter__zahl">{n}</span> : null}
              </button>
            );
          })}
        </div>
        <span className="liste-leiste__tasten leise sms-leiste__tasten">
          <kbd>j</kbd>/<kbd>k</kbd> · <kbd>Enter</kbd> öffnen · <kbd>u</kbd> ungelesen
        </span>
        <span className="sms-leiste__rechts">
          <span className="sms-suche">
            <Icon name="suche" groesse={13} />
            <input
              className="sms-suche__feld"
              type="search"
              value={suche}
              placeholder="Suchen …"
              aria-label="Mails durchsuchen (Absender, Betreff)"
              onChange={(e) => {
                setSuche(e.target.value);
                setParam("q", e.target.value.trim() || null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") (e.target as HTMLInputElement).blur();
              }}
            />
          </span>
        </span>
      </div>
      {fehler ? (
        <FehlerBox fehler={fehler} onNochmal={() => void laden()} />
      ) : null}
      {laedt && !liste.length ? (
        <Laden />
      ) : !fehler && !liste.length ? (
        <Leer titel={suchwort ? "Keine Treffer." : leerTexte[filter].titel} text={suchwort ? "Zu dieser Suche gibt es keine Mails im Filter." : leerTexte[filter].text} />
      ) : (
        <div className="aufgaben-liste aufgaben-liste--sms">
          {liste.map((m, i) => (
            <div key={m.id}>
              <MailZeileAnsicht mail={m} fokus={i === fokus} offen={offenId === m.id} onOeffnen={() => oeffnen(m, i)} onGelesen={() => void gelesenSetzen(m, !m.gelesen)} onLead={leadOeffnen} />
              {offenId === m.id ? (
                <MailDetailAnsicht
                  mail={m}
                  onGeaendert={(neu) => {
                    uebernehmen(neu);
                    onZaehler();
                  }}
                  onLead={leadOeffnen}
                  fehlerMeldung={(e, t) => meldung.fehler(e, t)}
                  ok={(t) => meldung.ok(t)}
                />
              ) : null}
            </div>
          ))}
          {cursor ? (
            <div className="tabelle__mehr">
              <button type="button" className="knopf" onClick={() => void mehr()}>
                Weitere laden
              </button>
            </div>
          ) : null}
        </div>
      )}
    </>
  );
}

function absender(m: MailZeile): string {
  return m.von.name || m.von.adresse;
}

function MailZeileAnsicht({ mail: m, fokus, offen, onOeffnen, onGelesen, onLead }: { mail: MailZeile; fokus: boolean; offen: boolean; onOeffnen: () => void; onGelesen: () => void; onLead: (id: string) => void }) {
  const ein = m.richtung === "eingehend";
  const neu = ein && !m.gelesen;
  const offenZuordnung = OFFEN.includes(m.zuordnung);
  return (
    <div
      className={`aufgabe sms-zeile mail-zeile${neu ? " sms-zeile--neu" : ""}${fokus ? " aufgabe--fokus" : ""}${offen ? " sms-zeile--offen" : ""}`}
      data-mail={m.id}
      aria-expanded={offen}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("button, a, input")) return;
        onOeffnen();
      }}
    >
      <span className={`aufgabe__haken aufgabe__haken--typ sms-kreis sms-kreis--${ein ? "mail" : "aus"}`} aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" focusable="false">
          {ein ? <path d="M3 6h18v12H3zM3 7l9 6 9-6" /> : <path d="M7 17L17 7M9 7h8v8" />}
        </svg>
      </span>
      <div className="sms-zeile__inhalt mail-zeile__inhalt">
        <span className="sms-zeile__lead">
          <span className="mail-zeile__von" title={`${m.von.name ?? ""} <${m.von.adresse}>`}>
            {absender(m)}
          </span>
        </span>
        <span className="sms-zeile__text" title={einzeilig(m.vorschau)}>
          <span className="mail-zeile__betreff">{m.betreff || "(ohne Betreff)"}</span>
          {m.vorschau ? <span className="mail-zeile__vorschau"> – {einzeilig(m.vorschau)}</span> : null}
        </span>
        <span className="sms-zeile__status">
          {m.lead_id ? (
            <a
              href={`/lead/${encodeURIComponent(m.lead_id)}`}
              className="aufgabe__leadname mail-zeile__lead"
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey) return;
                e.preventDefault();
                onLead(m.lead_id as string);
              }}
            >
              {m.lead_name ?? "Lead"}
            </a>
          ) : (
            <span className={`mail-zuordnung${offenZuordnung ? " mail-zuordnung--offen" : ""}`}>{ZUORDNUNG_TEXT[m.zuordnung]}</span>
          )}
        </span>
      </div>
      <span className="mail-zeile__anhang" title={m.hat_anhang ? "Mit Anhang" : undefined} aria-label={m.hat_anhang ? "Mit Anhang" : undefined}>
        {m.hat_anhang ? (
          <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" focusable="false" aria-hidden="true">
            <path d="M21 11.5l-8.6 8.6a5 5 0 0 1-7.1-7.1l8.6-8.6a3.3 3.3 0 0 1 4.7 4.7l-8.6 8.6a1.7 1.7 0 0 1-2.4-2.4l7.9-7.9" />
          </svg>
        ) : null}
      </span>
      <span className="aufgabe__datum" title={zeitpunktLang(m.datum)}>
        {nachrichtZeit(m.datum)}
      </span>
      <span className="aufgabe__aktionen sms-zeile__aktionen">
        {ein ? (
          <button type="button" className="symbolknopf" title={neu ? "Als gelesen markieren" : "Als ungelesen markieren (u)"} aria-label={neu ? "Als gelesen markieren" : "Als ungelesen markieren"} onClick={onGelesen}>
            <Icon name={neu ? "haken" : "mail"} groesse={14} />
          </button>
        ) : null}
      </span>
    </div>
  );
}

function MailDetailAnsicht({
  mail: m,
  onGeaendert,
  onLead,
  fehlerMeldung,
  ok,
}: {
  mail: MailZeile;
  onGeaendert: (m: MailZeile) => void;
  onLead: (id: string) => void;
  fehlerMeldung: (e: unknown, titel: string) => void;
  ok: (text: string) => void;
}) {
  const [detail, setDetail] = useState<MailDetail | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [wahl, setWahl] = useState(false);
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    let weg = false;
    setDetail(null);
    mailEingangApi
      .eine(m.id)
      .then((r) => !weg && setDetail(r.nachricht))
      .catch((e) => !weg && setFehler(e));
    return () => {
      weg = true;
    };
  }, [m.id]);

  const zuordnen = async (leadId: string, contactId?: string | null) => {
    setLaeuft(true);
    try {
      const r = await mailEingangApi.zuordnen(m.id, leadId, contactId ?? undefined);
      onGeaendert(r.nachricht);
      setWahl(false);
      ok(`Mail zugeordnet: ${r.nachricht.lead_name ?? "Lead"}.`);
    } catch (e) {
      fehlerMeldung(e, "Nicht zugeordnet");
    } finally {
      setLaeuft(false);
    }
  };

  const ignorieren = async () => {
    setLaeuft(true);
    try {
      const r = await mailEingangApi.ignorieren(m.id);
      onGeaendert(r.nachricht);
      ok("Mail ignoriert.");
    } catch (e) {
      fehlerMeldung(e, "Nicht gespeichert");
    } finally {
      setLaeuft(false);
    }
  };

  const wirdZugeordnet = OFFEN.includes(m.zuordnung);
  const an = (detail?.an ?? m.an).map((a) => a.name || a.adresse).join(", ");
  const cc = (detail?.cc ?? []).map((a) => a.name || a.adresse).join(", ");

  return (
    <div className="mail-detail" onClick={(e) => e.stopPropagation()}>
      <div className="mail-detail__kopf">
        <div className="mail-detail__betreff">{m.betreff || "(ohne Betreff)"}</div>
        <div className="mail-detail__zeilen leise">
          <span>Von {m.von.name ? `${m.von.name} <${m.von.adresse}>` : m.von.adresse}</span>
          <span>An {an || "—"}</span>
          {cc ? <span>Kopie {cc}</span> : null}
          <span>{zeitpunktLang(m.datum)}</span>
        </div>
      </div>
      {fehler ? (
        <FehlerBox fehler={fehler} kompakt />
      ) : !detail ? (
        <Laden klein />
      ) : (
        <>
          <pre className="mail-detail__text">{detail.text?.trim() || (detail.html ? "Diese Mail hat nur eine HTML-Fassung. Sie lässt sich auf der Lead-Seite im Verlauf ansehen." : "(kein Text)")}</pre>
          {detail.anhaenge.length ? (
            <div className="mail-detail__anhaenge">
              {detail.anhaenge
                .filter((a) => !a.inline_cid)
                .map((a) => (
                  <a key={a.datei_id} className="knopf knopf--klein" href={`/api/mail/dateien/${encodeURIComponent(a.datei_id)}`} target="_blank" rel="noopener noreferrer" title={`${a.dateiname} (${Math.max(1, Math.round(a.groesse / 1024))} KB)`}>
                    <Icon name="link" groesse={12} /> {a.dateiname}
                  </a>
                ))}
            </div>
          ) : null}
        </>
      )}
      {wahl ? (
        <LeadWahl start={m.von.name ?? m.von.adresse.split("@")[1]?.split(".")[0] ?? ""} onAbbrechen={() => setWahl(false)} onWahl={(t: SuchTreffer) => void zuordnen(t.lead_id, t.contact_id)} />
      ) : (
        <div className="sms-verlauf__fuss">
          {m.lead_id ? (
            <button type="button" className="knopf knopf--klein knopf--primaer" onClick={() => onLead(m.lead_id as string)}>
              Lead öffnen und antworten
            </button>
          ) : null}
          {m.zuordnung === "mehrdeutig" && m.kandidaten.length ? (
            <span className="mail-detail__kandidaten">
              <span className="leise">Zuordnen zu</span>
              {m.kandidaten.map((k) => (
                <button key={k.lead_id} type="button" className="knopf knopf--klein mail-detail__kandidat" disabled={laeuft} onClick={() => void zuordnen(k.lead_id)} title="Mail diesem Lead zuordnen">
                  {k.lead_name ?? k.lead_id}
                </button>
              ))}
            </span>
          ) : null}
          {wirdZugeordnet || m.zuordnung === "zugeordnet" ? (
            <button type="button" className="knopf knopf--klein" disabled={laeuft} onClick={() => setWahl(true)}>
              {m.lead_id ? "Anderem Lead zuordnen" : "Lead suchen und zuordnen"}
            </button>
          ) : null}
          {wirdZugeordnet ? (
            <button type="button" className="knopf knopf--klein knopf--leise" disabled={laeuft} onClick={() => void ignorieren()} title="Die Mail gehört zu keinem Lead und braucht nichts. Sie verschwindet aus der Liste Offen.">
              Ignorieren
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

